import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Typography, Paper, Box, CircularProgress, Alert } from '@mui/material';
import axios from 'axios';

// Landing page for the "Yes, keep me on the list" button in re-engagement emails.
// The guid is the email's tracking guid; confirming happens here (not on the link itself)
// so mail scanners that prefetch links can't confirm on the recipient's behalf.
const StaySubscribed: React.FC = () => {
    const { guid } = useParams<{ guid: string }>();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState(false);

    useEffect(() => {
        const confirmSubscription = async () => {
            if (!guid) {
                setError('Invalid link.');
                setLoading(false);
                return;
            }

            try {
                await axios.post(`/api/email/stay-subscribed/${guid}`);
                setSuccess(true);
            } catch (err) {
                console.error('Error confirming subscription:', err);
                setError(axios.isAxiosError(err) && err.response?.status === 404
                    ? 'This link is no longer valid.'
                    : 'We encountered an error while confirming your subscription. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        confirmSubscription();
    }, [guid]);

    return (
        <Container maxWidth="sm">
            <Box sx={{ mt: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Paper elevation={3} sx={{ p: 4, width: '100%', borderRadius: 2 }}>
                    <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                        {loading || success ? 'Thank You!' : 'Something Went Wrong'}
                    </Typography>

                    {loading ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                            <CircularProgress size={60} thickness={4} />
                            <Typography sx={{ mt: 2 }}>Processing your request...</Typography>
                        </Box>
                    ) : success ? (
                        <Box sx={{ py: 2 }}>
                            <Alert severity="success" sx={{ mb: 2 }}>
                                You're staying on our mailing list.
                            </Alert>
                            <Typography variant="body1" align="center">
                                We're glad you're still with us! You'll keep receiving news and announcements from Church of God Cincinnati.
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ py: 2 }}>
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error || 'An unexpected error occurred.'}
                            </Alert>
                            <Typography variant="body1" align="center">
                                If you'd like to stay on our mailing list, please reach out to us at (513) 755-0040.
                            </Typography>
                        </Box>
                    )}
                </Paper>
            </Box>
        </Container>
    );
};

export default StaySubscribed;
